// Shared token-refresh single-flight.
//
// There is exactly ONE in-flight refresh across the whole app. Both API layers —
// `HttpClient` (Layer A) and `modules/apiUtils` (Layer B) — call `refreshAuthToken()` here
// instead of each keeping their own `refreshTokenRequest`. This removes the cross-layer
// rotation race: the server rotates (invalidates) the refresh token on every refresh, so two
// concurrent refreshes (e.g. WeeklyCalendar via Layer A + Calendar/Map via Layer B) used to
// spend the same refresh token, invalidating the second and forcing a logout.
//
// This module ONLY refreshes + stores the new tokens and throws on failure. It does NOT clear
// the session or redirect — each caller keeps its own failure handling (HttpClient redirects,
// apiUtils throws), preserving existing behavior.

import { AppConstant } from "../config/AppConstants";
import { BASE_URL, GetRefreshToken_api } from "../config/ApiConfig";
import {
  getRefreshToken,
  getGuId,
  setAccessToken,
  setRefreshToken,
} from "../utils/SecurityService";

let refreshTokenRequest = null;

// Requests ONE fresh AccessToken/RefreshToken pair from the refresh endpoint and
// returns it WITHOUT persisting anything. Shared by `doRefresh` (which persists)
// and `fetchDeskSyncTokenPair` (which does not), so the network call lives in one
// place. `grantType` lets the backend distinguish a normal website refresh
// ("User") from a DeskSync handoff mint ("DeskSync").
const requestTokenPair = async (grantType = "User") => {
  const refreshResponse = await fetch(BASE_URL + GetRefreshToken_api, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      AppVersion: AppConstant.AppVersion,
      RefreshToken: getRefreshToken(),
      Language: AppConstant.Language,
      DeviceId: AppConstant.DeviceID,
      OsVersion: AppConstant.OsVersion,
      ClientId: AppConstant.DEFAULT_CLIENT_ID,
      GuId: getGuId(),
      ManufactureModel: AppConstant.ManufactureModel,
      GrantType: grantType,
    }),
  });

  if (!refreshResponse.ok) {
    throw new Error("Failed to refresh token");
  }

  const refreshData = await refreshResponse.json();
  const { AccessToken, RefreshToken } = refreshData.Data.Records;

  return { accessToken: AccessToken, refreshToken: RefreshToken };
};

const doRefresh = async () => {
  const tokens = await requestTokenPair();

  setAccessToken(tokens.accessToken);
  setRefreshToken(tokens.refreshToken);

  return tokens;
};

// DeskSync (app=desktop) handoff ONLY. Mints a fresh AccessToken/RefreshToken pair
// from the same endpoint but deliberately does NOT persist it to storage and does
// NOT join the shared single-flight: the new pair is embedded in the
// `ronspot-agent://` deep link handed to the DeskSync app, while the website keeps
// its own existing tokens in storage untouched. This gives DeskSync and the website
// distinct token pairs so the backend can tell where the same email is active
// (DeskSync / website / both). NOTE: whether minting this pair invalidates the
// website's existing refresh token is a backend/server-side concern.
export const fetchDeskSyncTokenPair = () => requestTokenPair("DeskSync");

// Returns the single shared in-flight refresh promise, creating it if none is running.
// Concurrent callers (within or across API layers) all await the same refresh.
export const refreshAuthToken = () => {
  if (!refreshTokenRequest) {
    refreshTokenRequest = doRefresh().finally(() => {
      refreshTokenRequest = null;
    });
  }
  return refreshTokenRequest;
};
